/* eslint-disable jsx-a11y/no-redundant-roles */
import { Paragraph, RoundButton, roundButtonStylesLarge } from "@smartsuite/react-ui/lib";
import { Formik, FormikHelpers, useFormikContext } from "formik";
import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";

import { getAssignedToMembers, getInternalAssignedToMembers } from "../../api";
import { getFormDefaultValues } from "../../helpers/formDefaults";
import { getOriginatingRecordParam } from "../../helpers/originatingRecord";
import { getFormAccentColorCss, isEnterpriseSolutionColor } from "../../helpers/formStyle";
import { FormConfig, FormValues } from "../../types/form";
import { FormikScrollToError } from "../FormikScrollToError/FormikScrollToError";
import { FormItems } from "../FormItems/FormItems";
import { FormTitle } from "../FormTitle/FormTitle";

import { createPortal } from "react-dom";
import { loadCheckpoint, saveCheckpointDebounced } from "../../helpers/checkpoints";
import { filterItemsByFeatureFlags, getLookupFieldSlugs } from "../../helpers/structure";
import { isSubmissionPage } from "../../helpers/submissionPage";
import { useFormContext } from "../../hooks/useFormContext";
import { useIsPreview } from "../../hooks/useIsPreview";
import { ProgressRestoreBanner } from "../ProgressRestore/ProgressRestoreBanner";
import { ProgressRestoreTooltip } from "../ProgressRestore/ProgressRestoreTooltip";
import { usePrefillContext } from "../../hooks/usePrefillContext";
import { MultiPageForm, MultiPageFormPage } from "../MultiPageForm/MultiPageForm";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { useFormPages } from "../../hooks/useFormPages";
import type { FormPage } from "../../types/form";
import { useProgressBarSlot } from "../PageContentBox/PageContentBox";

import "./Form.sass";

const DEFAULT_SUBMIT_LABEL = "Submit";

export interface FormProps {
  accountId: string;
  config: FormConfig;
  hash: string;
  onSubmit: (values: FormValues, formik: FormikHelpers<FormValues>) => Promise<void> | void;
}

export const Form: React.FC<FormProps> = ({ accountId, config, hash, onSubmit }) => {
  const formState = config?.form_state;
  const hasProgressRestore = formState?.has_progress_restore;
  const { isInternal, preferences, reportId } = useFormContext();
  const checkpointKey = isInternal && reportId ? reportId : hash;
  const { prefillValues } = usePrefillContext();
  const { isPreview } = useIsPreview();

  // Only internal forms are opened by a Button on a record; public forms ignore the marker.
  const formDefaults = getFormDefaultValues(
    config,
    preferences.timezone.value,
    isInternal ? getOriginatingRecordParam() : undefined
  );

  const inputPages = useMemo(
    () => (formState?.pages ?? []).filter((page) => !isSubmissionPage(page)),
    [formState]
  );

  const isPagesEnabled = config.feature_flags?.form_pages === true && inputPages.length > 0;

  const pages = useMemo(
    () =>
      isPagesEnabled
        ? inputPages.map((page) => ({
            ...page,
            items: filterItemsByFeatureFlags(page.items, config),
          }))
        : [],
    [isPagesEnabled, inputPages, config]
  );

  const dynamicSlugs = Object.keys(formDefaults.dynamicUserFields);
  const dynamicKey = useMemo(
    () => dynamicSlugs.flatMap((slug) => formDefaults.dynamicUserFields[slug]).join(","),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dynamicSlugs.join(",")]
  );

  const { data: resolvedUserDefaults } = useQuery(
    ["form-default-members", dynamicKey],
    async () => {
      const result: Record<string, unknown> = {};
      for (const slug of dynamicSlugs) {
        const memberIds = formDefaults.dynamicUserFields[slug];
        const members =
          isInternal && reportId
            ? await getInternalAssignedToMembers(accountId, reportId, slug)
            : config.sharing_hash
            ? await getAssignedToMembers(accountId, config.sharing_hash, slug, isPreview)
            : [];

        result[slug] = members
          .filter((m) => memberIds.includes(m.id))
          .map((m) => ({ id: m.id, name: m.name, member: m }));
      }
      return result;
    },
    {
      enabled: dynamicSlugs.length > 0,
      staleTime: Infinity,
    }
  );

  const initialValues = hasProgressRestore
    ? {
        ...formDefaults.simpleFields,
        ...resolvedUserDefaults,
        ...prefillValues.simpleFields,
        ...loadCheckpoint(accountId, checkpointKey, config?.application, config?.form_state.items),
      }
    : { ...formDefaults.simpleFields, ...resolvedUserDefaults, ...prefillValues.simpleFields };

  const handleSubmit = (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ): Promise<void> | void => {
    const lookupSlugs = getLookupFieldSlugs(config);
    const valuesWithoutLookup = Object.fromEntries(
      Object.entries(values).filter(([key]) => !lookupSlugs.has(key))
    );
    return onSubmit(valuesWithoutLookup as FormValues, formik);
  };

  return (
    <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
      <FormContent
        accountId={accountId}
        checkpointKey={checkpointKey}
        config={config}
        isPagesEnabled={isPagesEnabled}
        pages={pages}
        hasProgressRestore={!!hasProgressRestore}
      />
    </Formik>
  );
};

interface FormContentProps {
  accountId: string;
  checkpointKey: string;
  config: FormConfig;
  isPagesEnabled: boolean;
  pages: Array<{
    id: string;
    name: string;
    items: Array<import("../../types/form").FormConfigItem>;
  }>;
  hasProgressRestore: boolean;
}

const FormContent: React.FC<FormContentProps> = ({
  accountId,
  checkpointKey,
  config,
  isPagesEnabled,
  pages,
  hasProgressRestore,
}) => {
  const { isSubmitting, handleSubmit, status, values } = useFormikContext<FormValues>();
  const formState = config?.form_state;
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const formPages = useFormPages(pages as MultiPageFormPage[]);
  const progressBarConfig = formState?.style?.progress_bar;
  const showProgressBar =
    config.feature_flags?.form_progress_bar === true &&
    progressBarConfig?.enabled === true &&
    pages.length >= 2;

  const progressBarSlot = useProgressBarSlot();
  const shouldPortalProgressBar =
    showProgressBar && progressBarConfig != null && progressBarSlot != null;

  const progressAccentColor = useMemo(
    () =>
      getFormAccentColorCss(formState, config?.solution_color, {
        appearanceStyleEnabled: config.feature_flags?.form_appearance_style === true,
        useEnterprisePalette: isEnterpriseSolutionColor(config?.solution_color),
      }),
    [formState, config?.solution_color, config.feature_flags?.form_appearance_style]
  );

  useEffect(() => {
    if (!hasProgressRestore || isSubmitting) return;

    saveCheckpointDebounced(accountId, checkpointKey, valuesRef.current, config?.application);
  }, [hasProgressRestore, isSubmitting, accountId, checkpointKey, config?.application, values]);

  const progressBarElement = showProgressBar && progressBarConfig && (
    <ProgressBar
      pages={pages as FormPage[]}
      currentPageIndex={formPages.currentPageIndex}
      furthestVisitedIndex={formPages.furthestVisitedIndex}
      config={progressBarConfig}
      accentColor={progressAccentColor}
      onPageNavigate={formPages.goToPage}
    />
  );

  return (
    <form role="form" onSubmit={handleSubmit}>
      <FormikScrollToError />

      <FormTitle title={formState?.title} description={formState?.description} />

      {shouldPortalProgressBar &&
        progressBarSlot &&
        createPortal(progressBarElement, progressBarSlot)}

      {isPagesEnabled ? (
        <MultiPageForm
          config={config}
          pages={pages}
          formPages={formPages}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          values={values}
        />
      ) : (
        <>
          <FormItems
            config={config}
            formItems={filterItemsByFeatureFlags(formState?.items ?? [], config)}
            formValues={values}
          />
          <div className="form__footer">
            <RoundButton
              styles={roundButtonStylesLarge}
              loading={isSubmitting}
              onClick={() => handleSubmit()}
            >
              {formState?.submit_label ?? DEFAULT_SUBMIT_LABEL}
            </RoundButton>
          </div>
        </>
      )}

      {status === "error" && (
        <div className="form__unexpected-error">
          <br />
          <Paragraph color="error">
            An unexpected error has occurred, please try again later.
          </Paragraph>
        </div>
      )}

      {hasProgressRestore && <ProgressRestoreBanner />}
      {hasProgressRestore &&
        createPortal(
          <ProgressRestoreTooltip />,
          document.querySelector(".page-layout__content") ?? document.body
        )}
    </form>
  );
};
